import type { ListRatecardsQuery, Ratecard, RatecardSortField } from '@clockwise/clients/ratecards';
import { ApiError, coversDate, isoDateString } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Filter, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RatecardFormSheet } from '@/components/custom/ratecard-form-sheet';
import { SearchInput } from '@/components/custom/search-input';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ratecardsApi, serviceRolesApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = [
  'name',
  'effectiveFrom',
  'createdAt',
] as const satisfies readonly RatecardSortField[];

const listSearch = z.object({
  q: z.string().optional(),
  asOf: isoDateString().optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/ratecards/')({
  validateSearch: listSearch,
  component: RatecardsListPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: RatecardSortField = 'effectiveFrom';
const DEFAULT_SORT_DIR: 'asc' | 'desc' = 'desc';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function RatecardsListPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const sortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection = search.sortDirection ?? DEFAULT_SORT_DIR;
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const asOf = search.asOf ?? todayIso();

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({ search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }), replace: true });

  const canEdit = useHasPermission('ratecard.edit');
  const [sheetOpen, setSheetOpen] = useState(false);

  // Debounced text search.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => setSearchInput(q), [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListRatecardsQuery = {
    q: q.trim() || undefined,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.ratecards.list(listQuery),
    queryFn: () => ratecardsApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  // Active service roles back the create form (one rate row per role). Counts
  // are small (role *types*), so a single active list covers it.
  const serviceRolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list({
      active: true,
      internal: false,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 500,
    }),
    queryFn: () =>
      serviceRolesApi.list({
        active: true,
        internal: false,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 500,
      }),
    staleTime: 5 * 60_000,
    enabled: canEdit,
  });
  const serviceRoles = serviceRolesQuery.data?.data ?? [];

  const columns: ColumnDef<Ratecard>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 280,
      cell: ({ row }) => (
        <Link
          to="/setup/ratecards/$id"
          params={{ id: row.original.id }}
          className="block truncate font-medium text-primary underline-offset-2 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'effectiveFrom',
      header: 'Effective from',
      size: 160,
      cell: ({ row }) => row.original.effectiveFrom,
    },
    {
      id: 'effectiveTo',
      header: 'Effective to',
      enableSorting: false,
      size: 160,
      cell: ({ row }) =>
        row.original.effectiveTo ?? <span className="text-muted-foreground">open</span>,
    },
    {
      id: 'status',
      header: `Status as of ${asOf}`,
      enableSorting: false,
      size: 160,
      cell: ({ row }) => <StatusBadge card={row.original} asOf={asOf} />,
    },
  ];

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as RatecardSortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader
        title="Rate cards"
        subtitle="T&M rates per service role. One card is active on any given date."
        count={total}
      >
        {canEdit && (
          <Button onClick={() => setSheetOpen(true)} disabled={serviceRoles.length === 0}>
            <Plus />
            Add rate card
          </Button>
        )}
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search name…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <label htmlFor="rc-as-of" className="flex items-center gap-2 text-xs text-muted-foreground">
          Status as of
          <DatePicker
            id="rc-as-of"
            value={search.asOf ?? ''}
            onChange={(v) => updateSearch({ asOf: v || undefined })}
            placeholder="Today"
            clearable
            className="h-9 w-44"
          />
        </label>
      </div>

      <StickyTableScroll>
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id} style={{ width: h.getSize() }}>
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skel-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError
                    ? query.error.message
                    : 'Failed to load rate cards.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {q
                    ? 'No rate cards match your search.'
                    : 'No rate cards yet. Click “Add rate card”.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({ to: '/setup/ratecards/$id', params: { id: r.original.id } })
                  }
                >
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? (() => null), cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <RatecardFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        serviceRoles={serviceRoles}
        current={null}
      />
    </PageShell>
  );
}

// Same pill shape/colors as the shared `LifecycleStatusBadge` (activities
// catalog, customer activities panel) — not that component itself, since a
// rate card's status is a date-range check (Active/Scheduled/Expired), not
// the `LifecycleStatus` enum, but "Active" should still look identical
// everywhere it appears.
function StatusBadge({ card, asOf }: { card: Ratecard; asOf: string }) {
  if (coversDate({ effectiveFrom: card.effectiveFrom, effectiveTo: card.effectiveTo }, asOf)) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Active
      </span>
    );
  }
  const future = card.effectiveFrom > asOf;
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {future ? 'Scheduled' : 'Expired'}
    </span>
  );
}

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.asOf) out.asOf = s.asOf;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== DEFAULT_SORT_DIR) out.sortDirection = s.sortDirection;
  return out;
}
